import { Basket } from '@reward-platform/order-schemas/basket'
import { QueryClient, skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'
import { useAuthContext } from '~/context/auth'
import { useUser } from '~/context/user'
import { BasketRequestContext } from '~/services/basketService'
import { sortedAllRoutes } from '~/utils/checkoutRoutes'

export const CHECKOUT_BASKET_KEY = ['checkout-basket']
export const CHECKOUT_REDEEMABILITY_GATE_FLAG = 'checkout-redeemability-gate'

export type CheckoutBasket = {
  basket: Basket
  requestContext: BasketRequestContext
}

// Basket hooks share a QueryClient, so a mutation from any consumer must also
// invalidate validation started by another consumer.
const validationAttempts = new WeakMap<
  QueryClient,
  { controller: AbortController; requestContext: BasketRequestContext }
>()

export const cancelCheckoutValidation = (queryClient: QueryClient) => {
  validationAttempts.get(queryClient)?.controller.abort()
  validationAttempts.delete(queryClient)
}

export const resetCheckoutBasket = (queryClient: QueryClient) => {
  cancelCheckoutValidation(queryClient)
  queryClient.setQueryData(CHECKOUT_BASKET_KEY, null)
}

export const beginCheckoutValidation = (
  queryClient: QueryClient,
  requestContext: BasketRequestContext
) => {
  resetCheckoutBasket(queryClient)
  const controller = new AbortController()
  validationAttempts.set(queryClient, { controller, requestContext })

  return {
    signal: controller.signal,
    finish: () => {
      if (validationAttempts.get(queryClient)?.controller === controller) {
        validationAttempts.delete(queryClient)
      }
    },
  }
}

export const useCheckoutBasket = () => {
  const queryClient = useQueryClient()
  const pathname = usePathname()
  const { isAuthenticated } = useAuthContext()
  const { userCountry, userCurrency } = useUser()
  const { data: checkoutBasket } = useQuery<CheckoutBasket | null>({
    queryKey: CHECKOUT_BASKET_KEY,
    queryFn: skipToken,
    initialData: null,
    gcTime: 0,
  })

  useEffect(() => {
    const requestContext =
      checkoutBasket?.requestContext ?? validationAttempts.get(queryClient)?.requestContext
    if (
      requestContext &&
      (!isAuthenticated ||
        (pathname && !sortedAllRoutes.some(({ path }) => pathname.endsWith(path))) ||
        requestContext.countryCode !== userCountry ||
        requestContext.currencyCode !== userCurrency)
    ) {
      resetCheckoutBasket(queryClient)
    }
  }, [checkoutBasket, isAuthenticated, pathname, queryClient, userCountry, userCurrency])

  return checkoutBasket
}
